/**
 * Pure helpers + user-facing copy for the Safe Rollout panel (J2-I2).
 *
 * No React imports — jest-tested directly (utils/__tests__). Every
 * user-facing string constant lives in SAFE_ROLLOUT_UI_COPY /
 * SAFE_ROLLOUT_STATUS_BADGES / SAFE_ROLLOUT_GUARDRAIL_LABELS so the
 * banned-grammar sweep can cover the panel's entire vocabulary. Verdict
 * language stays DIRECTIONAL (J2-PRE-003): the panel reports what was
 * observed, never what produced it.
 */

import type {
  SafeRolloutGuardrailKey,
  SafeRolloutGuardrailThreshold,
  SafeRolloutLook,
  SafeRolloutRecord,
  SafeRolloutStatus,
} from '../types/safeRollout.types';

// ---------------------------------------------------------------------------
// Status → badge mapping (mock: MONITORING outlined-primary / NO REGRESSION
// DETECTED success / REGRESSION error / ROLLED BACK default)
// ---------------------------------------------------------------------------

export interface SafeRolloutStatusBadge {
  label: string;
  color: 'primary' | 'success' | 'error' | 'default';
  variant: 'outlined' | 'filled';
}

export const SAFE_ROLLOUT_STATUS_BADGES: Record<SafeRolloutStatus, SafeRolloutStatusBadge> = {
  monitoring: { label: 'Monitoring', color: 'primary', variant: 'outlined' },
  no_regression: { label: 'No regression detected', color: 'success', variant: 'filled' },
  regression_detected: { label: 'Regression', color: 'error', variant: 'filled' },
  rolled_back: { label: 'Rolled back', color: 'default', variant: 'filled' },
  cancelled: { label: 'Cancelled', color: 'default', variant: 'outlined' },
};

/** Short row labels per design mock (the backend labels are CLI-verbose). */
export const SAFE_ROLLOUT_GUARDRAIL_LABELS: Record<SafeRolloutGuardrailKey, string> = {
  funnel_completion: 'Funnel completion',
  js_errors: 'JS errors',
  payment_success: 'Payment success',
  refund_proxy: 'Refund proxy',
};

function guardrailLabel(key: SafeRolloutGuardrailKey): string {
  return SAFE_ROLLOUT_GUARDRAIL_LABELS[key] ?? key;
}

// ---------------------------------------------------------------------------
// User-facing copy (single source; swept by the banned-grammar test)
// ---------------------------------------------------------------------------

/** Verbatim J2-PRE-003 disclosure — ALWAYS visible on every rollout card. */
export const SAFE_ROLLOUT_DISCLOSURE = 'Directional monitoring — not a controlled experiment.';

export const SAFE_ROLLOUT_UI_COPY = {
  title: (versionId: string): string => `Safe rollout — ${versionId} → live`,
  disclosure: SAFE_ROLLOUT_DISCLOSURE,
  /** Green-state qualifier: the honest non-claim (shown next to the disclosure). */
  greenQualifier: (versionId: string): string =>
    `This does not prove ${versionId} is better — only that no guardrail regressed beyond its zero-harm bound.`,
  monitoringScope: (input: { versionId: string; watchingSince: string; previousVersionId: string }): string =>
    `Published ${input.versionId} at 100% · watching since ${input.watchingSince} · ` +
    `previous version ${input.previousVersionId} held for rollback · thresholds frozen at start`,
  closedScope: (input: { from: string; to: string; withinBounds: number; total: number }): string =>
    `Monitored ${input.from} → ${input.to} · ${input.withinBounds}/${input.total} guardrails stayed within bounds · monitoring closed`,
  rolledBackScope: (input: { previousVersionId: string; at: string }): string =>
    `Rolled back to ${input.previousVersionId} at ${input.at} · the previous version was republished to its public URL`,
  cancelledScope: 'Monitoring cancelled.',
  withinBound: 'within bound (one-sided sequential)',
  noLookYet: 'no look recorded yet',
  crossedRowTitle: (label: string): string => `${label} crossed its zero-harm bound`,
  crossedRowDetail: (input: { deltaPp: number | null; observations: number }): string => {
    const observed = `sequential bound crossed at obs ${input.observations.toLocaleString()}`;
    return input.deltaPp === null
      ? observed
      : `${formatPp(input.deltaPp)} vs reference · ${observed}`;
  },
  alertTitle: 'Regression signal',
  alertBody: (labels: string[]): string =>
    `${labels.join(', ')} crossed its zero-harm bound. Review the guardrails below and roll back to the previous version if the signal holds.`,
  rollbackButton: (previousVersionId: string): string => `Roll back to ${previousVersionId}`,
  evaluateButton: 'Evaluate now',
  emptyState: 'No safe rollouts yet — start one from the publish flow when releasing a new version.',
  loadErrorTitle: 'Could not load safe rollouts',
  sectionTitle: 'Safe rollouts',
} as const;

/** Signed percentage-point delta, 1 decimal, minus sign per mock ("−4.1pp"). */
export function formatPp(deltaPp: number): string {
  const rounded = Math.round(deltaPp * 10) / 10;
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '';
  return `${sign}${Math.abs(rounded).toFixed(1)}pp`;
}

/** "14:20 (2h 14m)" — start time plus elapsed, for the watching-since piece. */
export function formatWatchingSince(startedAtIso: string, nowMs: number): string {
  const started = new Date(startedAtIso);
  const time = started.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${time} (${formatElapsed(nowMs - started.getTime())})`;
}

export function formatElapsed(elapsedMs: number): string {
  const totalMinutes = Math.max(0, Math.floor(elapsedMs / 60_000));
  const days = Math.floor(totalMinutes / 1_440);
  const hours = Math.floor((totalMinutes % 1_440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/** "14 Jul 14:20" — closed-window endpoints. */
export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// ---------------------------------------------------------------------------
// Guardrail merge: thresholds.guardrails × LATEST look per guardrail
// ---------------------------------------------------------------------------

export interface SafeRolloutGuardrailRow {
  key: SafeRolloutGuardrailKey;
  label: string;
  referenceRate: number;
  zeroHarmDelta: number;
  /** The most recent look for this guardrail (by `at`; later entries win ties). */
  latestLook: SafeRolloutLook | null;
  /** True when the latest look crossed the one-sided sequential bound. */
  crossed: boolean;
  /** Observed − frozen reference, in percentage points; null without a look. */
  deltaPp: number | null;
}

/**
 * One display row per FROZEN threshold guardrail, merged with the LATEST look
 * recorded for that guardrail. Guardrails without looks yet render as
 * "no look recorded yet" (never faked as within-bound observations).
 */
export function mergeGuardrailsWithLooks(
  guardrails: SafeRolloutGuardrailThreshold[],
  looks: SafeRolloutLook[],
): SafeRolloutGuardrailRow[] {
  return guardrails.map((threshold) => {
    let latestLook: SafeRolloutLook | null = null;
    for (const look of looks) {
      if (look.guardrail !== threshold.key) continue;
      // `>=` so a later array entry wins an equal timestamp (append order).
      if (latestLook === null || look.at >= latestLook.at) latestLook = look;
    }
    const observedRate = latestLook && latestLook.observations > 0
      ? latestLook.conversions / latestLook.observations
      : null;
    return {
      key: threshold.key,
      label: guardrailLabel(threshold.key),
      referenceRate: threshold.referenceRate,
      zeroHarmDelta: threshold.zeroHarmDelta,
      latestLook,
      crossed: latestLook?.boundCrossed ?? false,
      deltaPp: observedRate === null
        ? null
        : Math.round((observedRate - threshold.referenceRate) * 1000) / 10,
    };
  });
}

/** Labels of every guardrail whose latest look crossed the bound. */
export function crossedGuardrailLabels(rows: SafeRolloutGuardrailRow[]): string[] {
  return rows.filter((row) => row.crossed).map((row) => row.label);
}

// ---------------------------------------------------------------------------
// Record-level presentation helpers
// ---------------------------------------------------------------------------

/** Open records — expanded by default in the panel list. */
export function isActiveSafeRollout(record: Pick<SafeRolloutRecord, 'status'>): boolean {
  return record.status === 'monitoring' || record.status === 'regression_detected';
}

/** Newest first (defensive — the backend already sorts by startedAt desc). */
export function sortSafeRolloutsNewestFirst(records: SafeRolloutRecord[]): SafeRolloutRecord[] {
  return [...records].sort((a, b) => (b.startedAt || '').localeCompare(a.startedAt || ''));
}

/** The one-line scope/summary sentence under the disclosure, per status. */
export function buildScopeLine(record: SafeRolloutRecord, nowMs: number): string {
  const rows = mergeGuardrailsWithLooks(record.thresholds.guardrails, record.looks);
  switch (record.status) {
    case 'no_regression':
      return SAFE_ROLLOUT_UI_COPY.closedScope({
        from: formatDateTime(record.startedAt),
        to: formatDateTime(record.closedAt ?? record.startedAt),
        withinBounds: rows.filter((row) => !row.crossed).length,
        total: rows.length,
      });
    case 'rolled_back':
      return SAFE_ROLLOUT_UI_COPY.rolledBackScope({
        previousVersionId: record.scope.previousVersionId,
        at: formatDateTime(record.rollback?.at ?? record.closedAt ?? record.startedAt),
      });
    case 'cancelled':
      return SAFE_ROLLOUT_UI_COPY.cancelledScope;
    case 'monitoring':
    case 'regression_detected':
    default:
      return SAFE_ROLLOUT_UI_COPY.monitoringScope({
        versionId: record.scope.versionId,
        watchingSince: formatWatchingSince(record.startedAt, nowMs),
        previousVersionId: record.scope.previousVersionId,
      });
  }
}
